import PptxGenJS from 'pptxgenjs';
import PropTypes from 'prop-types';
import { DownloadIcon } from '@utilities';

import LogRocket from 'logrocket';

import {
  createChartSlide,
  createImageSlide,
  createScatterChartSlide,
  createTableSlide,
  createTitleSlide,
} from './utilities';

export const downloadPowerPointFile = async ({ customFileName, data }) => {
  const { exportName, reportDateRange, reportName, reportType, reportUrl, shopperAttributes } =
    data;

  LogRocket.track('Flow_Ppt_Download');

  const pptx = new PptxGenJS();
  createTitleSlide({ pptx, reportName, reportType });
  // Loop through array of topics charts or handle only object with chart and table (report/survey scenario)
  const items = Array.isArray(data?.data) ? data.data : [data];
  for (const item of items ?? []) {
    const itemProps = {
      pptx,
      reportDateRange,
      reportType,
      reportUrl,
      shopperAttributes,
      slideTitle: item?.title || null,
    };
    switch (item.type) {
      case 'bar-chart':
      case 'bar-chart-two-labels':
      case 'bar-stack-chart':
      case 'column-chart':
        item.content?.map((chart) =>
          createChartSlide({
            chart: chart.data,
            readAs: chart.readAs,
            ...itemProps,
          })
        );
        break;
      case 'brand-tree-v2':
      case 'chart-image':
        await Promise.all(
          item.content?.map((chart) =>
            createImageSlide({
              chart: chart.data,
              readAs: chart.readAs,
              ...itemProps,
            })
          )
        );
        break;
      case 'scatter-chart':
        item.content?.map((chart) =>
          createScatterChartSlide({
            chart: chart.data,
            readAs: chart.readAs,
            ...itemProps,
          })
        );
        break;
      case 'table':
        item.content?.map((table) =>
          createTableSlide({
            ...itemProps,
            readAs: table.readAs,
            table: table.data,
          })
        );
        break;
      default: {
        const { chart, table } = item;
        const brokenBy = table?.columns[0]?.Header;
        if (chart)
          createChartSlide({
            brokenBy,
            chart,
            pptx,
            reportDateRange,
            reportType,
            reportUrl,
            shopperAttributes,
          });
        if (table)
          createTableSlide({
            pptx,
            reportDateRange,
            reportType,
            reportUrl,
            shopperAttributes,
            table,
          });
        break;
      }
    }
  }

  await pptx.writeFile({ fileName: `${customFileName || exportName}.pptx` });
};

const DownloadPowerPoint = ({ customFileName, data, downloadLabel }) => {
  const handleDownload = async () => {
    try {
      await downloadPowerPointFile({ customFileName, data });
    } catch (error) {
      console.error('Error generating PowerPoint:', error);
    }
  };

  return (
    <button
      aria-label="Download PowerPoint"
      data-testid="download-powerpoint-button"
      onClick={handleDownload}
    >
      <DownloadIcon />
      <span>.PPT File - {downloadLabel || 'Current Tab'}</span>
    </button>
  );
};

DownloadPowerPoint.propTypes = {
  customFileName: PropTypes.string,
  data: PropTypes.shape({
    chart: PropTypes.shape({
      format: PropTypes.string.isRequired,
      series: PropTypes.arrayOf(
        PropTypes.shape({
          data: PropTypes.array.isRequired,
          name: PropTypes.string.isRequired,
        })
      ).isRequired,
      xAxis: PropTypes.shape({
        categories: PropTypes.arrayOf(PropTypes.string).isRequired,
      }),
    }),
    data: PropTypes.arrayOf(PropTypes.object),
    exportName: PropTypes.string.isRequired,
    reportDateRange: PropTypes.arrayOf(PropTypes.string),
    reportName: PropTypes.string.isRequired,
    reportType: PropTypes.string.isRequired,
    reportUrl: PropTypes.string.isRequired,
    shopperAttributes: PropTypes.object,
  }).isRequired,
  downloadLabel: PropTypes.string,
};

export default DownloadPowerPoint;
